// Super Admin → RF Signatures. The company's view of what the app has learned:
// for each RF type, which EVENT BYTE means what (door = 9 Open, 26 Close), how
// many units confirm it, and where two units were labelled in CONFLICT.
//
// ⚠️ NOTHING IS STORED HERE. The signatures are DERIVED from the sensors already
// labelled across the estate (core/devices/rfSignatures.js) — this page reads
// and shows them. "Configuring" a type means labelling one reference sensor
// through the normal sensor dialog; its event bytes then recognise every later
// unit automatically. So this page is the place to CONFIRM the library and spot
// a bad reference (a conflict), not a form.
//
// ⚠️ GATED AT THE ROUTE AND HERE. A flag that only hides the menu tile is not a
// permission — both shells gate the route, and this redirects a non-admin too.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Card, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Chip,
  Tooltip, Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApp } from '../../../core/state/AppContext';
import { loadSignatures, refreshSignatures, signatureTable } from '../../../core/devices/rfSignatures';
import { rfTypeLabel, rfTypeIcon } from '../../../core/devices/rfTypes';

export default function RfSignaturesScreen() {
  const { isSuperAdmin } = useApp();
  const navigate = useNavigate();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);

  // Route-level gating backstops the menu: a non-admin who reaches the URL
  // directly is sent home rather than shown the estate's sensor data.
  useEffect(() => {
    if (!isSuperAdmin) navigate('/home', { replace: true });
  }, [isSuperAdmin, navigate]);

  const load = (force) => {
    setLoading(true);
    (force ? refreshSignatures() : loadSignatures())
      .then((sig) => setTable(signatureTable(sig)))
      .catch(() => setTable([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isSuperAdmin) load(false); }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const conflicts = (table || []).reduce(
    (n, t) => n + t.rows.filter((r) => r.conflict).length, 0);

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>RF Signatures</Typography>
          <Tooltip title="Reload from the latest labels">
            <IconButton color="inherit" onClick={() => load(true)}><RefreshIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          How a sensor is recognised at install. Each RF type&apos;s <b>event byte → meaning</b> is
          learned from sensors you have already labelled; a new unit with the same event bytes is
          detected automatically. To teach a type, label one reference sensor in the normal sensor
          dialog.
        </Alert>

        {conflicts > 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            {conflicts} event {conflicts === 1 ? 'byte is' : 'bytes are'} labelled two different ways
            (a reversed or mis-labelled reference). Those are <b>not</b> auto-filled at install — open
            the affected sensors and correct the meaning (the one-click reverse helps).
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : !table || table.length === 0 ? (
          <Alert severity="warning">
            No signatures learned yet. Label one reference sensor of each type (door, fire, motion…)
            and its event bytes will recognise every later unit.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {table.map((t) => (
              <TableContainer key={t.rfType} component={Card} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 20 }}>{rfTypeIcon(t.rfType)}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{rfTypeLabel(t.rfType)}</Typography>
                  <Chip size="small" label={`${t.rows.length} event${t.rows.length === 1 ? '' : 's'}`} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Event byte</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Means</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Units confirming</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {t.rows.map((r) => (
                      <TableRow key={r.byte} hover>
                        <TableCell><Chip size="small" variant="outlined" label={r.byte} /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.conflict ? '—' : r.meaning}</TableCell>
                        <TableCell align="center">{r.count}</TableCell>
                        <TableCell align="center">
                          {r.conflict
                            ? <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="conflict" />
                            : <Chip size="small" color="success" label="ok" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
